import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { BarChart } from '../../components/charts/BarChart.jsx';
import { ChartCard } from '../../components/charts/ChartCard.jsx';
import { DonutChart } from '../../components/charts/DonutChart.jsx';
import { LineChart } from '../../components/charts/LineChart.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { MetricCard } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';
import {
  toOrderStatusSeries,
  toPipelineSeries,
  toSalesTrendSeries,
  toTopCustomersSeries,
} from './salesCharts.js';

const MONTH_OPTIONS = [6, 12, 24];
const OPEN_STAGES = new Set(['LEAD', 'QUALIFY', 'PROPOSAL', 'NEGOTIATE']);
const sum = (arr) => arr.reduce((a, b) => a + b, 0);

export function SalesDashboardPage() {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const [months, setMonths] = useState(12);

  const trend = useQuery({ queryKey: ['sales', 'timeseries', months], queryFn: () => salesApi.timeseries({ months }).then((r) => r.items) });
  const orderStatus = useQuery({ queryKey: ['sales', 'order-status'], queryFn: () => salesApi.orderStatus().then((r) => r.items) });
  const pipeline = useQuery({ queryKey: ['sales', 'pipeline-stages'], queryFn: () => salesApi.pipelineStages().then((r) => r.items) });
  const topCustomers = useQuery({ queryKey: ['sales', 'top-customers'], queryFn: () => salesApi.topCustomers().then((r) => r.items) });

  const trendSeries = toSalesTrendSeries(trend.data?.months);
  const statusSeries = toOrderStatusSeries(orderStatus.data?.statuses);
  const pipelineSeries = toPipelineSeries(pipeline.data?.stages);
  const topSeries = toTopCustomersSeries(topCustomers.data?.customers);

  const openStages = (pipeline.data?.stages || []).filter((s) => OPEN_STAGES.has(s.stage));
  const totalRevenue = sum(trendSeries.revenue);
  const totalOrders = sum(trendSeries.orders);
  const openPipeline = openStages.reduce((a, s) => a + (Number(s.amount) || 0), 0);
  const weightedPipeline = openStages.reduce((a, s) => a + (Number(s.weighted) || 0), 0);

  const accent = palette?.accent || '#1d4ed8';
  const success = palette?.series?.[1] || '#059669';

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sales.dashboard.title')}</h1>
          <p className="muted">{t('sales.dashboard.subtitle')}</p>
        </div>
        <div className="period-switch">
          {MONTH_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === months ? 'period-btn active' : 'period-btn'}
              onClick={() => setMonths(m)}
            >
              {t('sales.dashboard.lastMonths', { count: m })}
            </button>
          ))}
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('sales.metric.revenue')} value={formatCurrency(totalRevenue)} />
        <MetricCard label={t('sales.metric.orders')} value={formatNumber(totalOrders, 0)} />
        <MetricCard label={t('sales.metric.openPipeline')} value={formatCurrency(openPipeline)} />
        <MetricCard label={t('sales.metric.weighted')} value={formatCurrency(weightedPipeline)} trendVariant="success" />
      </div>

      <div className="dashboard-grid">
        <ChartCard className="span-2" title={t('sales.chart.bookings')} subtitle={t('sales.chart.bookingsSub')}>
          {trend.isLoading ? <Spinner /> : trendSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <LineChart
              labels={trendSeries.labels}
              datasets={[{ label: t('sales.chart.revenue'), data: trendSeries.revenue, color: accent, fill: true }]}
            />
          )}
        </ChartCard>

        <ChartCard title={t('sales.chart.pipeline')}>
          {pipeline.isLoading ? <Spinner /> : pipelineSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={pipelineSeries.labels} datasets={[{ label: t('sales.chart.pipelineAmount'), data: pipelineSeries.amounts, color: accent }]} />
          )}
        </ChartCard>

        <ChartCard title={t('sales.chart.orderStatus')}>
          {orderStatus.isLoading ? <Spinner /> : statusSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <DonutChart labels={statusSeries.labels} values={statusSeries.counts} />
          )}
        </ChartCard>

        <ChartCard className="span-2" title={t('sales.chart.topCustomers')}>
          {topCustomers.isLoading ? <Spinner /> : topSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={topSeries.labels} datasets={[{ label: t('sales.metric.revenue'), data: topSeries.amounts, color: success }]} />
          )}
        </ChartCard>
      </div>
    </section>
  );
}
